import { useState, useMemo, useEffect } from 'react';
import { hasProFeatures } from '@/utils/entitlements';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { analytics } from '@/utils/analytics';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, Badge, VehicleImage } from '@/components';
import { useChatContext } from '@/context/ChatContext';
import {
  useSession,
  useCommLogJson,
  useSecondBySecondData,
  useNhtsaVinDecode,
  useReports,
  useEntitlement,
} from '@/hooks/useProApi';
import {
  formatVehicle,
  formatVin,
  formatDuration,
  utcToLocal,
  type PidStatistics,
  type RichDtcInfo,
  type SecondBySecondRecord,
  type VehicleAnalysisSession,
  type FreezeFrameAISnapshot,
  type ReadinessGroupDtc,
  type DtcExtendedRecord,
} from '@/types';

// Chart colors
const CHART_COLORS = ['#6d7cff', '#e866dd', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16'];

// Section header component
function SectionHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h3 className="text-lg font-bold text-white">{title}</h3>
      {count !== undefined && (
        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400">
          {count}
        </span>
      )}
    </div>
  );
}

// Label-value pair component
function LabelValue({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-white font-medium break-words">{value}</p>
    </div>
  );
}

// DTC Table component
// The ECU's own status bits for a code, one chip each. Only OBDonUDS vehicles report these; this
// is what the old always-"unknown" Severity column should have been.
function DtcStatusChips({ status }: { status?: ReadinessGroupDtc }) {
  if (!status) return <span className="text-gray-600">—</span>;
  const chips: { label: string; tone: string }[] = [];
  if (status.mil_requested) chips.push({ label: 'MIL requested', tone: 'bg-red-500/20 text-red-300' });
  if (status.confirmed) chips.push({ label: 'Confirmed', tone: 'bg-orange-500/20 text-orange-300' });
  if (status.test_failed) chips.push({ label: 'Test failed', tone: 'bg-orange-500/20 text-orange-300' });
  if (status.pending) chips.push({ label: 'Pending', tone: 'bg-yellow-500/20 text-yellow-300' });
  chips.push(status.complete_since_clear
    ? { label: 'Monitor ran since clear', tone: 'bg-green-500/20 text-green-300' }
    : { label: 'Monitor not run since clear', tone: 'bg-gray-500/20 text-gray-300' });
  return (
    <div className="flex flex-wrap gap-1" title={`ISO 14229 statusOfDTC ${status.status} (${status.full_code})`}>
      {chips.map(c => (
        <span key={c.label} className={`px-2 py-0.5 rounded-full text-xs ${c.tone}`}>{c.label}</span>
      ))}
    </div>
  );
}

function DtcTable({ dtcs, type, ecuStatus }: {
  dtcs: RichDtcInfo[];
  type: 'stored' | 'pending' | 'permanent';
  ecuStatus?: Map<string, ReadinessGroupDtc>;   // present = OBDonUDS session
}) {
  const typeConfig = {
    stored: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'Stored (Mode 03)' },
    pending: { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', label: 'Pending (Mode 07)' },
    permanent: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', label: 'Permanent (Mode 0A)' },
  };
  const config = typeConfig[type];

  if (dtcs.length === 0) {
    return (
      <div className={`rounded-xl p-4 ${config.bg} border ${config.border}`}>
        <p className={`font-medium ${config.text} mb-2`}>{config.label}</p>
        <p className="text-gray-500 text-sm">No {type} codes</p>
      </div>
    );
  }

  return (
    <div className={`rounded-xl overflow-hidden border ${config.border}`}>
      <div className={`px-4 py-2 ${config.bg}`}>
        <p className={`font-medium ${config.text}`}>{config.label} ({dtcs.length})</p>
      </div>
      <table className="w-full">
        <thead>
          <tr className="bg-black/20 text-left">
            <th className="px-4 py-2 text-xs text-gray-400 font-medium">Code</th>
            <th className="px-4 py-2 text-xs text-gray-400 font-medium">Description</th>
            {ecuStatus && <th className="px-4 py-2 text-xs text-gray-400 font-medium">ECU status</th>}
          </tr>
        </thead>
        <tbody>
          {dtcs.map((dtc, idx) => (
            <tr key={`${dtc.code}-${idx}`} className="border-t border-white/5">
              <td className="px-4 py-3 font-mono font-bold text-white">{dtc.code}</td>
              <td className="px-4 py-3 text-gray-300 text-sm">{dtc.description || 'No description'}</td>
              {ecuStatus && <td className="px-4 py-3"><DtcStatusChips status={ecuStatus.get(dtc.code)} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Status PIDs (fuel system status, monitor status, ...) decode to a run of "state: True/False"
// pairs, which rendered as one unreadable paragraph. Parse any such value into groups
// ("Fuel System 1", "Fuel System 2") and their flags.
type FlagGroup = { header: string | null; flags: { label: string; on: boolean }[] };

function parseFlagList(value: unknown): FlagGroup[] | null {
  if (typeof value !== 'string') return null;

  // The decoder writes one item per LINE: a group header on its own ("Fuel System 1:") and then
  // "state: True/False" lines. HTML collapses the line breaks, which is why it read as a paragraph.
  // A value that arrives on a single line is split before each header / after each True|False.
  const lines = value
    .replace(/(True|False)\s+(?=\S)/g, '$1\n')
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);

  const groups: FlagGroup[] = [];
  let flagCount = 0;
  for (const line of lines) {
    const flag = line.match(/^(.*):\s*(True|False)$/);
    if (!flag) {
      groups.push({ header: line.replace(/:\s*$/, ''), flags: [] });
      continue;
    }
    let label = flag[1].trim();
    // Single-line form: "Fuel System 2: Open loop - ..." carries its header inline.
    const inlineHead = label.match(/^([^:]{1,40}):\s*(.+)$/);
    if (inlineHead) {
      groups.push({ header: inlineHead[1].trim(), flags: [] });
      label = inlineHead[2].trim();
    }
    if (groups.length === 0) groups.push({ header: null, flags: [] });
    groups[groups.length - 1].flags.push({ label, on: flag[2] === 'True' });
    flagCount++;
  }
  return flagCount >= 2 ? groups.filter(g => g.flags.length > 0) : null;
}

// One line per state that is ON; the states that are off are counted, not listed.
function FlagListValue({ groups }: { groups: FlagGroup[] }) {
  return (
    <div className="space-y-2 text-right">
      {groups.map((g, gi) => {
        const on = g.flags.filter(f => f.on);
        const off = g.flags.length - on.length;
        return (
          <div key={gi}>
            {g.header && <p className="text-xs text-gray-500 uppercase tracking-wide">{g.header}</p>}
            {on.length > 0 ? (
              on.map((f, fi) => (
                <p key={fi} className="text-white font-medium">{f.label}</p>
              ))
            ) : (
              <p className="text-gray-400">None active</p>
            )}
            {off > 0 && (
              <p className="text-gray-500 text-xs" title={g.flags.filter(f => !f.on).map(f => f.label).join('\n')}>
                {off} other {off === 1 ? 'state' : 'states'} off
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// One readable line per OBDonUDS extended data record.
function describeExtendedRecord(r: DtcExtendedRecord): React.ReactNode {
  const f = r.fields || {};
  const num = (v: unknown) => (typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 3 }) : String(v ?? '—'));
  switch (r.record) {
    case '0x90':
      return <>Fault has been active for <b className="text-white">{num(f.b1_counter_hours)} h</b> of engine operation</>;
    case '0x91':
      return <>Monitor completed <b className="text-white">{num(f.monitor_completion_counts)}</b> times in <b className="text-white">{num(f.monitor_condition_encountered_counts)}</b> opportunities (ratio <b className="text-white">{num(f.iumpr_ratio)}</b>)</>;
    case '0x93':
      return <>Monitor activity numerator <b className="text-white">{num(f.monitor_activity_numerator)}</b>, stored ratio <b className="text-white">{num(f.stored_monitor_activity_ratio)}</b></>;
    case '0x92': {
      const results = (f.test_results as Record<string, unknown>[] | undefined) || [];
      return (
        <span className="block">
          {results.length} test result{results.length === 1 ? '' : 's'} (raw, unscaled)
          {results.map((t, i) => {
            const v = Number(t.test_value_raw), lo = Number(t.min_limit_raw), hi = Number(t.max_limit_raw);
            const out = v < lo || v > hi;
            return (
              <span key={i} className="block ml-3">
                value <b className={out ? 'text-red-300' : 'text-white'}>{num(v)}</b> · limits {num(lo)} to {num(hi)}
                <span className="text-gray-500"> · unit/scaling {String(t.unit_and_scaling_id)}</span>
                {out && <span className="text-red-300"> · outside limits</span>}
              </span>
            );
          })}
        </span>
      );
    }
    default:
      return <>{Object.entries(f).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${num(v)}`).join(' · ') || r.raw_hex || '—'}</>;
  }
}

// OBDonUDS extended DTC data, grouped by code.
function ExtendedDtcDataCard({ records }: { records: DtcExtendedRecord[] }) {
  const byDtc = new Map<string, DtcExtendedRecord[]>();
  for (const r of records) {
    const key = r.full_code || r.dtc;
    byDtc.set(key, [...(byDtc.get(key) || []), r]);
  }
  return (
    <div className="space-y-3">
      {[...byDtc.entries()].map(([code, recs]) => (
        <div key={code} className="rounded-xl border border-blue-500/30 overflow-hidden">
          <div className="px-4 py-2 bg-blue-500/10">
            <span className="font-mono font-bold text-white">{code}</span>
          </div>
          <table className="w-full">
            <tbody>
              {recs.map((r, i) => (
                <tr key={i} className="border-t border-white/5 align-top">
                  <td className="px-4 py-3 text-gray-400 text-sm w-72">
                    {r.name} <span className="font-mono text-gray-600">{r.record}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-sm">{describeExtendedRecord(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// Freeze Frame snapshot card — one per frame (one per triggering DTC)
function FreezeFrameCard({ frame }: { frame: FreezeFrameAISnapshot }) {
  const sensorEntries = Object.entries(frame.sensor_data || {});
  return (
    <div className="rounded-xl overflow-hidden border border-purple-500/30">
      <div className="px-4 py-2 bg-purple-500/10">
        <p className="font-medium text-purple-300">
          <span className="font-mono font-bold text-white mr-2">{frame.triggering_dtc || '—'}</span>
          {frame.triggering_dtc_description || 'Unknown DTC'}
          <span className="text-purple-400/70 text-xs ml-2">(Frame {frame.frame_number})</span>
        </p>
      </div>
      {sensorEntries.length > 0 ? (
        <table className="w-full">
          <thead>
            <tr className="bg-black/20 text-left">
              <th className="px-4 py-2 text-xs text-gray-400 font-medium w-20">PID</th>
              <th className="px-4 py-2 text-xs text-gray-400 font-medium">Description</th>
              <th className="px-4 py-2 text-xs text-gray-400 font-medium text-right">Value</th>
            </tr>
          </thead>
          <tbody>
            {sensorEntries.map(([pid, reading]) => (
              <tr key={pid} className="border-t border-white/5">
                <td className="px-4 py-3 font-mono text-gray-400 text-sm">{pid}</td>
                <td className="px-4 py-3 text-gray-300 text-sm">{reading.description}</td>
                {(() => {
                  const flagGroups = parseFlagList(reading.value);
                  return flagGroups ? (
                    <td className="px-4 py-3"><FlagListValue groups={flagGroups} /></td>
                  ) : (
                    <td className="px-4 py-3 text-white font-medium text-right">
                      {reading.value}
                      {reading.units ? <span className="text-gray-400 ml-1">{reading.units}</span> : null}
                    </td>
                  );
                })()}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="px-4 py-3 text-gray-500 text-sm">No sensor data captured in this frame.</p>
      )}
    </div>
  );
}

// Format a millisecond span the same way formatDuration formats a date pair.
// Used for the telemetry-derived session duration (#107).
function formatSpan(diffMs: number): string {
  if (!Number.isFinite(diffMs) || diffMs < 0) return '-';
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  if (minutes > 0) return `${minutes} min`;
  return `${seconds} sec`;
}

// Parse JSONL second-by-second data
function parseSecondBySecondData(jsonlText: string): SecondBySecondRecord[] {
  if (!jsonlText) return [];
  const lines = jsonlText.trim().split('\n').filter(Boolean);
  const records: SecondBySecondRecord[] = [];
  for (const line of lines) {
    try {
      records.push(JSON.parse(line));
    } catch {
      // Skip invalid lines
    }
  }
  return records;
}

// Standard SAE J1979 Mode-01 PID names. Fallback labels for when the rich
// CommLog analysis blob is missing (no clean Disconnect -> no pid_statistics),
// so the telemetry chart still shows "Engine RPM" instead of a bare "0x0C".
const STANDARD_PID_NAMES: Record<string, string> = {
  '0x04': 'Calculated Engine Load',
  '0x05': 'Engine Coolant Temperature',
  '0x06': 'Short Term Fuel Trim B1',
  '0x07': 'Long Term Fuel Trim B1',
  '0x08': 'Short Term Fuel Trim B2',
  '0x09': 'Long Term Fuel Trim B2',
  '0x0A': 'Fuel Pressure',
  '0x0B': 'Intake Manifold Pressure',
  '0x0C': 'Engine RPM',
  '0x0D': 'Vehicle Speed',
  '0x0E': 'Timing Advance',
  '0x0F': 'Intake Air Temperature',
  '0x10': 'MAF Air Flow Rate',
  '0x11': 'Throttle Position',
  '0x1F': 'Run Time Since Engine Start',
  '0x21': 'Distance With MIL On',
  '0x22': 'Fuel Rail Pressure (rel.)',
  '0x23': 'Fuel Rail Gauge Pressure',
  '0x2F': 'Fuel Tank Level',
  '0x33': 'Barometric Pressure',
  '0x42': 'Control Module Voltage',
  '0x43': 'Absolute Load Value',
  '0x44': 'Commanded Air-Fuel Ratio',
  '0x45': 'Relative Throttle Position',
  '0x46': 'Ambient Air Temperature',
  '0x47': 'Absolute Throttle Position B',
  '0x49': 'Accelerator Pedal Position D',
  '0x4A': 'Accelerator Pedal Position E',
  '0x5C': 'Engine Oil Temperature',
  '0x5E': 'Engine Fuel Rate',
};

// Missing Telemetry Warning Banner
function TelemetryWarningBanner({ type }: { type: 'full' | 'charts-only' }) {
  const [expanded, setExpanded] = useState(false);

  if (type === 'charts-only') {
    return (
      <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
        <svg className="w-5 h-5 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-amber-300 text-sm">
          Telemetry charts unavailable — second-by-second data was not captured for this session.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1">
            <p className="text-amber-300 font-medium">No detailed telemetry captured for this session.</p>
            <p className="text-amber-200/70 text-sm mt-1">
              To ensure complete data, tap <strong>Disconnect</strong> before closing the app.
              More robust auto-upload is coming in a future update.
            </p>
          </div>
        </div>
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2 text-left text-sm text-amber-400 hover:bg-amber-500/10 border-t border-amber-500/20 flex items-center gap-2"
      >
        <svg className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        Why does this happen?
      </button>
      {expanded && (
        <div className="px-4 pb-4 text-sm text-amber-200/60">
          <p>
            Phones may close the app before uploads finish. Tapping <strong>Disconnect</strong> guarantees
            the session data is saved to the cloud before the connection ends.
          </p>
        </div>
      )}
    </div>
  );
}

export function SessionDetail() {
  const { key } = useParams<{ key: string }>();
  const { data: session, isLoading, error } = useSession(key || '');
  const { openChat, sessionKey: activeChatSession, isOpen: chatIsOpen } = useChatContext();

  // Rich session data from CommLogProcessor
  const { data: commLogData, isLoading: commLogLoading } = useCommLogJson(session?.vin, key);
  const { data: secondBySecondRaw, isLoading: sbsLoading } = useSecondBySecondData(key);

  // Auto-decode VIN using free NHTSA API (cached by VIN)
  const { data: nhtsaVehicleInfo, isLoading: nhtsaLoading } = useNhtsaVinDecode(session?.vin);

  // State for chart PID selection
  const [selectedPids, setSelectedPids] = useState<string[]>([]);

  // State for collapsible Vehicle Information section
  const [vehicleInfoExpanded, setVehicleInfoExpanded] = useState(false);

  // Analytics: Track session viewed
  useEffect(() => {
    if (session && key) {
      analytics.sessionViewed(session.vin, key);
    }
  }, [session, key]);

  // Analytics: Track commlog loaded
  useEffect(() => {
    if (commLogData?.sessions?.length) {
      analytics.commlogLoaded(commLogData.metadata?.vin || null, commLogData.sessions.length);
    }
  }, [commLogData]);

  // Find the matching session from CommLogJson by session_id
  const richSession: VehicleAnalysisSession | undefined = useMemo(() => {
    if (!commLogData?.sessions || !key) return undefined;
    // Strictly the requested session. The old `|| sessions[0]` fallback silently rendered a
    // DIFFERENT session's data when the clicked one wasn't in the (VIN-wide) decode set (#134);
    // with ?sessionKey= the decode is scoped to this session, and a no-data session must show
    // "no data", never someone else's.
    return commLogData.sessions.find(s => s.session_id?.toLowerCase() === key.toLowerCase());
  }, [commLogData, key]);

  // A car with no stored freeze frame still answers Mode 02 PID 02 (with DTC 0000), which used to
  // come back as a placeholder frame: no triggering DTC, no sensor data. That is "nothing captured",
  // not a freeze frame, so it must not draw a card reading "Unknown DTC".
  const freezeFrames = useMemo(
    () => (richSession?.freeze_frame?.frames ?? []).filter(
      f => f.triggering_dtc || Object.keys(f.sensor_data ?? {}).length > 0),
    [richSession]);

  // OBDonUDS: each code's ECU status bits, looked up by code. Undefined on a classic OBD-II
  // session, which hides the ECU-status column there.
  const ecuDtcStatus = useMemo(() => {
    const groups = richSession?.vehicle_state?.readiness_groups;
    if (!groups) return undefined;
    const map = new Map<string, ReadinessGroupDtc>();
    for (const g of Object.values(groups))
      for (const d of g.dtcs || []) if (!map.has(d.code)) map.set(d.code, d);
    return map;
  }, [richSession]);
  const extendedDtcRecords = richSession?.dtc_data?.extended_records ?? [];

  // Reports saved for THIS session, newest first. A session can have several (one had 16), and the
  // sessions list only says "Saved", so until now there was no way to reach them from here.
  const { data: reportsData } = useReports();
  const { data: entitlement } = useEntitlement();
  const sessionReports = useMemo(
    () => (reportsData?.reports ?? [])
      .filter(r => r.sessionKey?.toLowerCase() === key?.toLowerCase())
      .sort((a, b) => utcToLocal(b.generatedAt).getTime() - utcToLocal(a.generatedAt).getTime()),
    [reportsData, key]);

  // Parse second-by-second data
  const secondBySecondRecords = useMemo(() => {
    if (!secondBySecondRaw) return [];
    return parseSecondBySecondData(secondBySecondRaw);
  }, [secondBySecondRaw]);

  // Build chart data from second-by-second records
  const { chartData, availablePids } = useMemo(() => {
    if (secondBySecondRecords.length === 0) return { chartData: [], availablePids: [] };

    const pidInfo = new Map<string, string>(); // pid -> units
    const timeSeriesMap: Record<string, Record<string, number>> = {};

    for (const record of secondBySecondRecords) {
      const ts = utcToLocal(record.timestamp).getTime();
      if (!timeSeriesMap[ts]) timeSeriesMap[ts] = { time: ts };

      for (const reading of record.data) {
        // Mode 09 (VIN, calibration IDs) is identity, not telemetry; it has no business on a chart.
        if (/^s09_/i.test(reading.pid)) continue;
        timeSeriesMap[ts][reading.pid] = reading.value;
        pidInfo.set(reading.pid, reading.units);
      }
    }

    const data = Object.values(timeSeriesMap).sort((a, b) => (a.time as number) - (b.time as number));
    const pids = Array.from(pidInfo.entries()).map(([pid, units]) => ({ pid, units }));

    // Auto-select first 3 PIDs
    if (selectedPids.length === 0 && pids.length > 0) {
      // Never default to the DTC-flag channels (keys like "s03_0x00_P0300"): they are constant
      // booleans that draw as flat lines and made the chart look like it captured nothing (#80).
      // Prefer the live sensors people expect to see first, then fill from the rest.
      const isRealSensor = (p: string) => /^0x[0-9A-Fa-f]{2}$/.test(p);
      const priority = ['0x0C', '0x0D', '0x04', '0x11', '0x05', '0x10']; // RPM, Speed, Load, Throttle, Coolant, MAF
      const real = pids.map(p => p.pid).filter(isRealSensor);
      const ordered = [...priority.filter(p => real.includes(p)), ...real.filter(p => !priority.includes(p))];
      setSelectedPids((ordered.length ? ordered : pids.map(p => p.pid)).slice(0, 3));
    }

    return { chartData: data, availablePids: pids };
  }, [secondBySecondRecords, selectedPids.length]);

  // Detect missing telemetry data
  const hasCommLogData = useMemo(() => {
    if (commLogLoading) return true; // Don't show warning while loading
    if (!commLogData) return false;
    if (!commLogData.sessions || commLogData.sessions.length === 0) return false;
    // Check if the session has meaningful data (pid_statistics)
    const session = commLogData.sessions.find(s => s.session_id?.toLowerCase() === key?.toLowerCase());
    return session && Object.keys(session.pid_statistics || {}).length > 0;
  }, [commLogData, commLogLoading, key]);

  const hasSecondBySecondData = useMemo(() => {
    if (sbsLoading) return true; // Don't show warning while loading
    return secondBySecondRecords.length > 0;
  }, [secondBySecondRecords, sbsLoading]);

  // Supported PIDs - use array for list, descriptions dict for display
  // Must be placed before early returns to maintain consistent hook order
  const supportedPidsData = useMemo(() => {
    const pidsArray = richSession?.supported_pids;
    const descriptions = richSession?.supported_pid_descriptions;

    if (!pidsArray || !Array.isArray(pidsArray)) return [];

    // Map array to objects with descriptions from the separate dictionary
    return pidsArray.map((pid: string) => ({
      pid,
      description: descriptions?.[pid] || undefined,
    }));
  }, [richSession?.supported_pids, richSession?.supported_pid_descriptions]);

  // Calculate overall readiness: if any SUPPORTED monitor is NOT ready, overall is not ready
  // Must be placed before early returns to maintain consistent hook order
  const calculatedOverallReadiness = useMemo(() => {
    const monitors = richSession?.vehicle_state?.readiness;
    if (!monitors || typeof monitors !== 'object') return undefined;
    const monitorEntries = Object.entries(monitors);
    if (monitorEntries.length === 0) return undefined;

    // Only consider supported monitors
    const supportedMonitors = monitorEntries.filter(([, m]) => m?.supported === true);
    if (supportedMonitors.length === 0) return true; // No supported monitors = ready

    // If any supported monitor is not complete, not ready
    return supportedMonitors.every(([, m]) => m?.complete === true);
  }, [richSession?.vehicle_state?.readiness]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#6d7cff] to-[#e866dd] flex items-center justify-center animate-pulse">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <p className="text-gray-400">Loading session...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-bold text-white mb-2">Session Not Found</h2>
        <Link to="/sessions" className="text-purple-400 hover:text-purple-300">Back to Sessions</Link>
      </div>
    );
  }

  // Metadata from CommLogJson

  // Session data - prefer rich session data, fallback to basic session
  // One definition of "how many codes" (#162, #191): DISTINCT codes across stored + pending +
  // permanent. A code that is stored AND pending AND permanent is one fault, not three. The decoded
  // session is the truth when we have it; the uploaded summary is the fallback.
  const dtcCount = richSession?.dtc_data
    ? new Set(
        [
          ...(richSession.dtc_data.stored_dtcs || []),
          ...(richSession.dtc_data.pending_dtcs || []),
          ...(richSession.dtc_data.permanent_dtcs || []),
        ].map(d => d.code)
      ).size
    : session.dtcs?.length || session.dtcCount || 0;

  const monitors = session.monitors || [];
  const totalMonitors = monitors.length || (session.readyMonitorsCount || 0) + (session.notReadyMonitorsCount || 0);
  const milStatus = richSession?.mil_status ?? session.milStatus;
  // Duration from the actual telemetry extent (first->last second-by-second
  // sample) when we have it. session.createdAt/updatedAt only bracket the
  // initial connect/scan burst, so a 14-minute drive reads as "15 sec" (#107).
  // Fall back to the metrics window only when telemetry is absent/too sparse.
  const duration = chartData.length >= 2
    ? formatSpan((chartData[chartData.length - 1].time as number) - (chartData[0].time as number))
    : formatDuration(session.createdAt, session.updatedAt);

  // PID Statistics from rich session
  const pidStats = richSession?.pid_statistics || {};

  // Helper: resolve PID description for both old ("0x15") and new ("0x15_OxygenSensorVoltage") key formats
  const getPidDescription = (pid: string): string => {
    // Direct match (old format or if pidStats already has the full key)
    if (pidStats[pid]?.description) return pidStats[pid].description;

    // Standard-PID fallback so labels survive a missing CommLog analysis blob
    // (case-insensitive on the hex digits: feed may emit 0x0c or 0x0C)
    const norm = pid.length > 2 ? '0x' + pid.slice(2).toUpperCase() : pid;
    if (STANDARD_PID_NAMES[norm]) return STANDARD_PID_NAMES[norm];

    // DTC-presence flags, any bucket and any code family (P/C/B/U):
    // "s03_0x00_U0127" -> "DTC Present: U0127", "s07_..." -> pending, "s0A_..." -> permanent.
    const dtcMatch = pid.match(/^s(03|07|0A)_0x00_([PCBU][0-9A-Za-z]{4})$/i);
    if (dtcMatch) {
      const bucket = { '03': 'DTC Present', '07': 'Pending DTC Present', '0A': 'Permanent DTC Present' }[dtcMatch[1].toUpperCase()];
      return `${bucket}: ${dtcMatch[2].toUpperCase()}`;
    }

    // New format: extract base PID and suffix
    const underscoreIdx = pid.indexOf('_');
    if (underscoreIdx > 0) {
      const basePid = pid.substring(0, underscoreIdx);
      const suffix = pid.substring(underscoreIdx + 1)
        .replace(/([A-Z])/g, ' $1')  // "OxygenSensorVoltage" → " Oxygen Sensor Voltage"
        .trim();
      const baseDesc = pidStats[basePid]?.description;
      return baseDesc ? `${baseDesc} — ${suffix}` : `${basePid} ${suffix}`;
    }

    return pid;
  };

  // Readiness monitors from rich session
  const richMonitors = richSession?.vehicle_state?.readiness || {};

  return (
    <div className="space-y-6">
      {/* Back link + Chat button */}
      <div className="flex items-center justify-between">
        <Link to="/sessions" className="inline-flex items-center text-gray-400 hover:text-white transition group">
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Sessions
        </Link>
        <button
          onClick={() => key && openChat(key)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl
            bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium
            hover:shadow-lg hover:shadow-purple-500/25 hover:scale-[1.02] transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {chatIsOpen && activeChatSession === key ? 'View Chat' : 'Chat with ARIA'}
        </button>
      </div>

      {/* 1. SESSION SUMMARY CARD */}
      <Card>
        <SectionHeader title="Session Summary" />
        <div className="flex flex-col sm:flex-row gap-5">
          <VehicleImage vin={session.vin} size="md" />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 flex-1 min-w-0">
          <LabelValue
            label="Vehicle"
            value={formatVehicle(session.vehicleYear, session.vehicleMake, session.vehicleModel)}
            className="lg:col-span-2"
          />
          <LabelValue label="VIN" value={<span className="font-mono">{formatVin(session.vin)}</span>} />
          <LabelValue label="Session ID" value={<span className="font-mono text-xs">{session.sessionKey}</span>} className="lg:col-span-2" />

          <LabelValue label="Start Time" value={format(utcToLocal(session.createdAt), 'MMM d, yyyy h:mm a')} />
          <LabelValue label="End Time" value={session.updatedAt ? format(utcToLocal(session.updatedAt), 'MMM d, yyyy h:mm a') : '-'} />
          <LabelValue label="Duration" value={<span className="text-purple-400 font-bold">{duration}</span>} />
          <LabelValue
            label="MIL Status"
            value={
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${milStatus ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                {milStatus ? 'ON' : 'OFF'}
              </span>
            }
          />
          <LabelValue
            label="DTC Count"
            value={
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${dtcCount > 0 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>
                {dtcCount}
              </span>
            }
          />
          </div>
        </div>

        {commLogLoading && <p className="text-gray-500 text-sm mt-4">Loading rich session data...</p>}
      </Card>

      {/* 1.4 REPORTS saved for this session */}
      {(sessionReports.length > 0 || hasProFeatures(entitlement?.plan)) && (
      <Card>
        <SectionHeader title="Reports" count={sessionReports.length} />
        <div className="flex flex-wrap gap-2">
          {sessionReports.map(r => {
            const when = utcToLocal(r.generatedAt);
            return (
              <Link
                key={r.reportId}
                to={`/reports/${r.reportId}`}
                title={r.reportTitle}
                className="w-44 px-3 py-2 rounded-xl bg-purple-500/15 border border-purple-400/30 hover:bg-purple-500/25 hover:border-purple-400/60 transition-colors"
              >
                <span className="flex items-center gap-2 text-sm font-medium text-white">
                  <svg className="w-4 h-4 shrink-0 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  {isNaN(when.getTime()) ? 'Report' : format(when, 'MMM d, yyyy')}
                </span>
                <span className="block text-xs text-gray-400 mt-0.5 pl-6">
                  {isNaN(when.getTime()) ? '' : format(when, 'h:mm a')}
                  {r.reportType ? ` · ${r.reportType}` : ''}
                </span>
              </Link>
            );
          })}
          {hasProFeatures(entitlement?.plan) && key && (
            <Link
              to={`/reports/generate?sessionKey=${encodeURIComponent(key)}`}
              className="w-44 px-3 py-2 rounded-xl border border-dashed border-white/30 hover:border-purple-400/60 hover:bg-white/5 transition-colors"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-200">
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                New report
              </span>
              <span className="block text-xs text-gray-400 mt-0.5 pl-6">Generate with ARIA</span>
            </Link>
          )}
        </div>
      </Card>
      )}

      {/* 1.5 VEHICLE INFORMATION (NHTSA decode - auto-loaded, collapsed by default) */}
      {session.vin && (
        <Card>
          <button
            onClick={() => setVehicleInfoExpanded(!vehicleInfoExpanded)}
            className="w-full flex items-center justify-between text-left group"
          >
            <h3 className="text-lg font-bold text-white">Vehicle Information</h3>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${vehicleInfoExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {nhtsaLoading ? (
            <p className="text-gray-500 text-sm mt-4">Loading vehicle details...</p>
          ) : nhtsaVehicleInfo && vehicleInfoExpanded ? (
            <div className="space-y-6 mt-4">
              {/* Core Vehicle Info */}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Vehicle</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <LabelValue label="Year" value={nhtsaVehicleInfo.year || 'N/A'} />
                  <LabelValue label="Make" value={nhtsaVehicleInfo.make || 'N/A'} />
                  <LabelValue label="Model" value={nhtsaVehicleInfo.model || 'N/A'} />
                  <LabelValue label="Trim" value={nhtsaVehicleInfo.trim || 'N/A'} />
                  {nhtsaVehicleInfo.series && <LabelValue label="Series" value={nhtsaVehicleInfo.series} />}
                  <LabelValue label="Body Type" value={nhtsaVehicleInfo.bodyClass || 'N/A'} />
                  <LabelValue label="Doors" value={nhtsaVehicleInfo.doors || 'N/A'} />
                  {nhtsaVehicleInfo.vehicleType && <LabelValue label="Vehicle Type" value={nhtsaVehicleInfo.vehicleType} />}
                </div>
              </div>

              {/* Engine & Powertrain */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Engine & Powertrain</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {nhtsaVehicleInfo.engineModel && <LabelValue label="Engine" value={nhtsaVehicleInfo.engineModel} />}
                  <LabelValue label="Cylinders" value={nhtsaVehicleInfo.engineCylinders || 'N/A'} />
                  <LabelValue label="Displacement" value={nhtsaVehicleInfo.displacementL ? `${nhtsaVehicleInfo.displacementL}L` : 'N/A'} />
                  {nhtsaVehicleInfo.engineHp && <LabelValue label="Horsepower" value={`${nhtsaVehicleInfo.engineHp} HP`} />}
                  {nhtsaVehicleInfo.engineConfiguration && <LabelValue label="Configuration" value={nhtsaVehicleInfo.engineConfiguration} />}
                  {nhtsaVehicleInfo.turbo && <LabelValue label="Turbo" value={nhtsaVehicleInfo.turbo} />}
                  <LabelValue label="Transmission" value={nhtsaVehicleInfo.transmissionStyle || 'N/A'} />
                  {nhtsaVehicleInfo.transmissionSpeeds && <LabelValue label="Speeds" value={nhtsaVehicleInfo.transmissionSpeeds} />}
                  <LabelValue label="Drivetrain" value={nhtsaVehicleInfo.driveType || 'N/A'} />
                  <LabelValue label="Fuel Type" value={nhtsaVehicleInfo.fuelTypePrimary || 'N/A'} />
                  {nhtsaVehicleInfo.fuelTypeSecondary && <LabelValue label="Secondary Fuel" value={nhtsaVehicleInfo.fuelTypeSecondary} />}
                  {nhtsaVehicleInfo.fuelInjectionType && <LabelValue label="Fuel Injection" value={nhtsaVehicleInfo.fuelInjectionType} />}
                </div>
              </div>

              {/* Manufacturer Info */}
              <div className="pt-4 border-t border-white/10">
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Manufacturer</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <LabelValue label="Manufacturer" value={nhtsaVehicleInfo.manufacturer || 'N/A'} />
                  {nhtsaVehicleInfo.plantCity && <LabelValue label="Plant City" value={nhtsaVehicleInfo.plantCity} />}
                  {nhtsaVehicleInfo.plantState && <LabelValue label="Plant State" value={nhtsaVehicleInfo.plantState} />}
                  <LabelValue label="Plant Country" value={nhtsaVehicleInfo.plantCountry || 'N/A'} />
                </div>
              </div>

              {/* Weight & Dimensions - only show if data exists */}
              {(nhtsaVehicleInfo.gvwr || nhtsaVehicleInfo.curbWeightLb || nhtsaVehicleInfo.wheelBaseLong) && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Weight & Dimensions</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {nhtsaVehicleInfo.gvwr && <LabelValue label="GVWR" value={nhtsaVehicleInfo.gvwr} />}
                    {nhtsaVehicleInfo.curbWeightLb && <LabelValue label="Curb Weight" value={`${nhtsaVehicleInfo.curbWeightLb} lbs`} />}
                    {nhtsaVehicleInfo.wheelBaseLong && <LabelValue label="Wheelbase" value={nhtsaVehicleInfo.wheelBaseLong} />}
                    {nhtsaVehicleInfo.bedLength && <LabelValue label="Bed Length" value={nhtsaVehicleInfo.bedLength} />}
                    {nhtsaVehicleInfo.bedType && <LabelValue label="Bed Type" value={nhtsaVehicleInfo.bedType} />}
                    {nhtsaVehicleInfo.cabType && <LabelValue label="Cab Type" value={nhtsaVehicleInfo.cabType} />}
                  </div>
                </div>
              )}

              {/* Electric Vehicle Info - only show for EVs */}
              {nhtsaVehicleInfo.electrificationLevel && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Electric Vehicle</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <LabelValue label="Electrification" value={nhtsaVehicleInfo.electrificationLevel} />
                    {nhtsaVehicleInfo.batteryType && <LabelValue label="Battery Type" value={nhtsaVehicleInfo.batteryType} />}
                    {nhtsaVehicleInfo.batteryKwh && <LabelValue label="Battery Capacity" value={`${nhtsaVehicleInfo.batteryKwh} kWh`} />}
                    {nhtsaVehicleInfo.batteryV && <LabelValue label="Battery Voltage" value={`${nhtsaVehicleInfo.batteryV}V`} />}
                    {nhtsaVehicleInfo.chargerLevel && <LabelValue label="Charger Level" value={nhtsaVehicleInfo.chargerLevel} />}
                    {nhtsaVehicleInfo.chargerPowerKw && <LabelValue label="Charger Power" value={`${nhtsaVehicleInfo.chargerPowerKw} kW`} />}
                    {nhtsaVehicleInfo.evDriveUnit && <LabelValue label="EV Drive Unit" value={nhtsaVehicleInfo.evDriveUnit} />}
                  </div>
                </div>
              )}

              {/* Safety Features - only show if any exist */}
              {(nhtsaVehicleInfo.abs || nhtsaVehicleInfo.esc || nhtsaVehicleInfo.airBagLocFront) && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-3">Safety Features</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {nhtsaVehicleInfo.abs && <LabelValue label="ABS" value={nhtsaVehicleInfo.abs} />}
                    {nhtsaVehicleInfo.esc && <LabelValue label="ESC" value={nhtsaVehicleInfo.esc} />}
                    {nhtsaVehicleInfo.traction && <LabelValue label="Traction Control" value={nhtsaVehicleInfo.traction} />}
                    {nhtsaVehicleInfo.tpms && <LabelValue label="TPMS" value={nhtsaVehicleInfo.tpms} />}
                    {nhtsaVehicleInfo.airBagLocFront && <LabelValue label="Front Airbags" value={nhtsaVehicleInfo.airBagLocFront} />}
                    {nhtsaVehicleInfo.airBagLocSide && <LabelValue label="Side Airbags" value={nhtsaVehicleInfo.airBagLocSide} />}
                    {nhtsaVehicleInfo.airBagLocCurtain && <LabelValue label="Curtain Airbags" value={nhtsaVehicleInfo.airBagLocCurtain} />}
                    {nhtsaVehicleInfo.blindSpotMon && <LabelValue label="Blind Spot Monitor" value={nhtsaVehicleInfo.blindSpotMon} />}
                    {nhtsaVehicleInfo.forwardCollisionWarning && <LabelValue label="Forward Collision" value={nhtsaVehicleInfo.forwardCollisionWarning} />}
                    {nhtsaVehicleInfo.laneDepartureWarning && <LabelValue label="Lane Departure" value={nhtsaVehicleInfo.laneDepartureWarning} />}
                    {nhtsaVehicleInfo.rearVisibilitySystem && <LabelValue label="Rear Camera" value={nhtsaVehicleInfo.rearVisibilitySystem} />}
                    {nhtsaVehicleInfo.adaptiveCruiseControl && <LabelValue label="Adaptive Cruise" value={nhtsaVehicleInfo.adaptiveCruiseControl} />}
                  </div>
                </div>
              )}

              {/* Error/Note info if present */}
              {nhtsaVehicleInfo.errorText && (
                <div className="pt-4 border-t border-white/10">
                  <p className="text-amber-400 text-sm">{nhtsaVehicleInfo.errorText}</p>
                </div>
              )}
            </div>
          ) : !nhtsaLoading && !vehicleInfoExpanded ? (
            <p className="text-gray-500 text-sm mt-2">Click to expand vehicle details</p>
          ) : !nhtsaLoading ? (
            <p className="text-gray-500 text-sm mt-4">Vehicle details unavailable</p>
          ) : null}
        </Card>
      )}

      {/* MISSING TELEMETRY WARNING */}
      {!commLogLoading && !hasCommLogData && (
        <TelemetryWarningBanner type="full" />
      )}

      {/* 2. DIAGNOSTIC TROUBLE CODES - Only show if CommLog data exists OR we have basic session DTCs */}
      {(hasCommLogData || (session.dtcs && session.dtcs.length > 0)) && (
      <Card>
        <SectionHeader title="Diagnostic Trouble Codes" count={dtcCount} />

        {richSession?.dtc_data ? (
          <div className="space-y-4">
            <DtcTable dtcs={richSession.dtc_data.stored_dtcs || []} type="stored" ecuStatus={ecuDtcStatus} />
            <DtcTable dtcs={richSession.dtc_data.pending_dtcs || []} type="pending" ecuStatus={ecuDtcStatus} />
            <DtcTable dtcs={richSession.dtc_data.permanent_dtcs || []} type="permanent" ecuStatus={ecuDtcStatus} />

            <div className="flex items-center gap-2 mt-4 p-3 rounded-lg bg-black/20">
              <span className="text-gray-400 text-sm">Has Emissions DTCs:</span>
              <Badge variant={richSession.dtc_data.has_emissions_dtcs ? 'error' : 'success'} size="sm">
                {richSession.dtc_data.has_emissions_dtcs ? 'Yes' : 'No'}
              </Badge>
            </div>
          </div>
        ) : commLogLoading ? (
          // Still decoding: say so. This used to fall through to "No Trouble Codes" beside a
          // non-zero count badge on every load (#191).
          <div className="text-center py-8 text-gray-400">Loading trouble codes…</div>
        ) : session.dtcs && session.dtcs.length > 0 ? (
          <div className="space-y-3">
            {session.dtcs.map((dtc, idx) => (
              <div key={`${dtc.code}-${idx}`} className="flex items-start gap-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <span className="font-mono font-bold text-white text-lg">{dtc.code}</span>
                <span className="text-gray-300">{dtc.description || 'No description'}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-400 font-medium">No Trouble Codes</p>
          </div>
        )}
      </Card>
      )}

      {/* 2a. EXTENDED DTC DATA - OBDonUDS (SAE J1979-2) vehicles only */}
      {extendedDtcRecords.length > 0 && (
      <Card>
        <SectionHeader title="Extended DTC Data" count={extendedDtcRecords.length} />
        <p className="text-gray-400 text-sm mb-4">
          Reported per code by OBDonUDS (2023+) vehicles: how long the fault has been active, how often its monitor has run, and its last test result against the ECU's own limits.
        </p>
        <ExtendedDtcDataCard records={extendedDtcRecords} />
      </Card>
      )}

      {/* 2b. FREEZE FRAME - Only show if the session captured Mode 02 snapshots */}
      {freezeFrames.length > 0 && (
      <Card>
        <SectionHeader title="Freeze Frame" count={freezeFrames.length} />
        <p className="text-gray-400 text-sm mb-4">
          Sensor values the PCM snapshotted the moment each DTC set — the conditions present when the fault occurred.
        </p>
        <div className="space-y-4">
          {freezeFrames.map((frame) => (
            <FreezeFrameCard key={frame.frame_number} frame={frame} />
          ))}
        </div>
      </Card>
      )}

      {/* 3. READINESS MONITORS - Only show if CommLog data exists */}
      {hasCommLogData && (
      <Card>
        <SectionHeader title="Readiness Monitors" count={totalMonitors} />

        {Object.keys(richMonitors).length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(richMonitors).map(([name, state]) => {
              const isReady = state.complete;
              const isSupported = state.supported;
              return (
                <div
                  key={name}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 ${
                    !isSupported ? 'bg-gray-500/10 border-gray-500/40' :
                    isReady ? 'bg-green-500/10 border-green-500/40' : 'bg-yellow-500/10 border-yellow-500/40'
                  }`}
                >
                  <div className={`text-2xl mb-1 ${!isSupported ? 'text-gray-500' : isReady ? 'text-green-400' : 'text-yellow-400'}`}>
                    {!isSupported ? '—' : isReady ? '✓' : '○'}
                  </div>
                  <div className="text-xs text-gray-400 text-center leading-tight">{name}</div>
                  <div className={`text-[10px] mt-1 ${!isSupported ? 'text-gray-500' : isReady ? 'text-green-400' : 'text-yellow-400'}`}>
                    {!isSupported ? 'N/A' : isReady ? 'Ready' : 'Not Ready'}
                  </div>
                </div>
              );
            })}
          </div>
        ) : monitors.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {monitors.map((monitor, idx) => {
              const isReady = monitor.status?.toLowerCase() === 'ready';
              return (
                <div
                  key={`${monitor.monitorName}-${idx}`}
                  className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 ${isReady ? 'bg-green-500/10 border-green-500/40' : 'bg-yellow-500/10 border-yellow-500/40'}`}
                >
                  <div className={`text-2xl mb-1 ${isReady ? 'text-green-400' : 'text-yellow-400'}`}>{isReady ? '✓' : '○'}</div>
                  <div className="text-xs text-gray-400 text-center">{monitor.monitorName}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No monitor data available</p>
        )}

        {calculatedOverallReadiness !== undefined && (
          <div className="mt-4 p-3 rounded-lg bg-black/20 flex items-center gap-2">
            <span className="text-gray-400 text-sm">Overall Readiness:</span>
            <Badge variant={calculatedOverallReadiness ? 'success' : 'warning'} size="sm">
              {calculatedOverallReadiness ? 'PASS' : 'NOT READY'}
            </Badge>
          </div>
        )}
      </Card>
      )}

      {/* 4. PID STATISTICS TABLE - Only shows if pidStats has data (naturally hidden when no CommLog) */}
      {Object.keys(pidStats).length > 0 && (
        <Card>
          <SectionHeader title="PID Statistics" count={Object.keys(pidStats).length} />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-black/20 text-left">
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium">PID</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium">Description</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium text-right">Min</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium text-right">Max</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium text-right">Avg</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium text-right">Std Dev</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium text-right">Samples</th>
                  <th className="px-4 py-3 text-xs text-gray-400 font-medium">Units</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(pidStats).map(([pid, stats]: [string, PidStatistics]) => (
                  <tr key={pid} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3 font-mono text-purple-400">{pid}</td>
                    <td className="px-4 py-3 text-white text-sm">{stats.description}</td>
                    <td className="px-4 py-3 text-green-400 text-right font-mono">{stats.min_value?.toFixed(1)}</td>
                    <td className="px-4 py-3 text-red-400 text-right font-mono">{stats.max_value?.toFixed(1)}</td>
                    <td className="px-4 py-3 text-blue-400 text-right font-mono">{stats.avg_value?.toFixed(1)}</td>
                    <td className="px-4 py-3 text-gray-400 text-right font-mono">{stats.std_dev?.toFixed(2)}</td>
                    <td className="px-4 py-3 text-gray-300 text-right">{stats.sample_count}</td>
                    <td className="px-4 py-3 text-gray-400">{stats.units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* 5. SUPPORTED PIDs - Grid showing hex codes with descriptions */}
      {supportedPidsData.length > 0 && (
        <Card>
          <SectionHeader title="Supported PIDs" count={supportedPidsData.length} />
          <div className="max-h-80 overflow-y-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {supportedPidsData.map(({ pid, description }) => (
                <div
                  key={pid}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10"
                >
                  <span className="font-mono text-purple-400 text-sm flex-shrink-0">{pid}</span>
                  <span className="text-gray-500">—</span>
                  <span className="text-gray-300 text-sm truncate" title={description}>
                    {description || 'Unknown PID'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* 6. TELEMETRY CHARTS */}
      {/* Show charts-only warning when CommLog exists but second-by-second data is missing */}
      {hasCommLogData && !sbsLoading && !hasSecondBySecondData && (
        <TelemetryWarningBanner type="charts-only" />
      )}
      {chartData.length > 0 && (
        <Card>
          <SectionHeader title="Telemetry Charts (Second-by-Second)" count={secondBySecondRecords.length} />

          <div className="mb-4">
            <p className="text-sm text-gray-400 mb-2">Select PIDs to display (max 8):</p>
            <div className="flex flex-wrap gap-2">
              {availablePids.map(({ pid, units }) => {
                // Get description from pid_statistics, truncate if too long
                const description = getPidDescription(pid);
                const shortName = description.length > 25 ? description.slice(0, 22) + '...' : description;
                return (
                  <button
                    key={pid}
                    onClick={() => {
                      analytics.telemetryChartInteraction('toggle_pid', pid);
                      setSelectedPids((prev) =>
                        prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid].slice(-8)
                      );
                    }}
                    className={`px-3 py-1.5 text-xs rounded-lg transition ${
                      selectedPids.includes(pid)
                        ? 'bg-purple-500/30 text-purple-300 border border-purple-500'
                        : 'bg-white/5 text-gray-400 border border-white/10 hover:border-white/30'
                    }`}
                    title={units && units !== 'bool' ? `${pid}: ${description} (${units})` : `${pid}: ${description}`}
                  >
                    {units && units !== 'bool' ? `${shortName} (${units})` : shortName}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="h-96 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <defs>
                  <linearGradient id="chartGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#6d7cff" />
                    <stop offset="100%" stopColor="#e866dd" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="time"
                  tickFormatter={(t) => format(new Date(t), 'h:mm:ss a')}
                  stroke="#666"
                  tick={{ fontSize: 10 }}
                />
                <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e1e2e', border: '1px solid #333', borderRadius: '8px' }}
                  labelFormatter={(t) => format(new Date(t), 'h:mm:ss a')}
                  formatter={(value: number) => typeof value === 'number' ? value.toFixed(1) : value}
                />
                <Legend />
                {selectedPids.map((pid, idx) => {
                  const units = availablePids.find(p => p.pid === pid)?.units || '';
                  const description = getPidDescription(pid);
                  return (
                    <Line
                      key={pid}
                      type="monotone"
                      dataKey={pid}
                      stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      name={units && units !== 'bool' ? `${description} (${units})` : description}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {sbsLoading && (
        <Card>
          <p className="text-gray-400 text-center py-8">Loading telemetry data...</p>
        </Card>
      )}

      {/* Vehicle Profile Link - Prominent CTA to full vehicle profile */}
      {session.vin && (
        <Card>
          <Link
            to={`/vehicles/${session.vin}`}
            className="flex items-center justify-between p-2 rounded-xl hover:bg-white/5 transition group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6d7cff] to-[#e866dd] flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-lg">View full vehicle profile</p>
                <p className="text-gray-400 text-sm">Market value, title check, auction history, maintenance & more</p>
              </div>
            </div>
            <svg
              className="w-6 h-6 text-gray-400 group-hover:text-white group-hover:translate-x-1 transition-all"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </Card>
      )}

    </div>
  );
}
